import React, { useMemo } from 'react';
import { Card, Typography, List, Tag } from 'antd';
import {
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { LogLevel } from '@/domain/value-objects/LogLevel';
import type { SystemLogData, SystemLogEntry } from '../types/dashboard.types';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { LOG_LEVEL_COLORS } from '@/shared/constants/colors';
import { formatDateTimeShort } from '@/shared/utils/dateFormatter';

const { Title, Text } = Typography;

interface SystemLogPanelProps {
  readonly logData: SystemLogData;
}

const LOG_LEVEL_CONFIG = {
  info: {
    icon: <InfoCircleOutlined />,
    color: LOG_LEVEL_COLORS.INFO,
    tagColor: 'blue',
    label: '情報',
  },
  warning: {
    icon: <WarningOutlined />,
    color: LOG_LEVEL_COLORS.WARNING,
    tagColor: 'orange',
    label: '警告',
  },
  error: {
    icon: <CloseCircleOutlined />,
    color: LOG_LEVEL_COLORS.ERROR,
    tagColor: 'red',
    label: 'エラー',
  },
} as const;

const SYSTEM_LOG_PANEL_TITLE_LEVEL = 4;
const LOG_PANEL_HEIGHT_DESKTOP = 400;
const LOG_PANEL_HEIGHT_MOBILE = 300;

export const SystemLogPanel: React.FC<SystemLogPanelProps> = ({
  logData,
}) => {
  const { isMobile } = useResponsive();

  // ディープコピーし、structuredCloneで失われる値オブジェクト（LogLevel）を復元
  const restoredLogs = useMemo((): readonly SystemLogEntry[] => {
    return logData.logs.map((log) => ({
      ...log,
      timestamp: new Date(log.timestamp.getTime()),
      level: LogLevel.create(log.level.toKey()),
    }));
  }, [logData]);

  const formatTimestamp = (timestamp: Date): string => {
    return formatDateTimeShort(timestamp);
  };

  const getLevelConfig = (level: LogLevel) => {
    return LOG_LEVEL_CONFIG[level.toKey() as 'info' | 'warning' | 'error'];
  };

  const renderLogItem = (logEntry: SystemLogEntry): React.ReactNode => {
    const levelConfig = getLevelConfig(logEntry.level);
    const formattedTimestamp = formatTimestamp(logEntry.timestamp);

    return (
      <List.Item key={logEntry.logId} className="px-3 py-2 border-b border-gray-100">
        <div className="w-full">
          <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'} mb-1`}>
            <div className="flex items-center gap-2">
              <span style={{ color: levelConfig.color }}>
                {levelConfig.icon}
              </span>
              <Tag color={levelConfig.tagColor} className="text-xs">
                {levelConfig.label}
              </Tag>
              {!isMobile && logEntry.source && (
                <Text type="secondary" className="text-xs">
                  [{logEntry.source}]
                </Text>
              )}
            </div>
            <Text type="secondary" className="text-xs">
              {formattedTimestamp}
            </Text>
          </div>
          <div className="text-sm">
            {logEntry.message}
          </div>
          {isMobile && logEntry.source && (
            <Text type="secondary" className="text-xs mt-1">
              ソース: {logEntry.source}
            </Text>
          )}
        </div>
      </List.Item>
    );
  };

  return (
    <Card className="h-full">
      <Title level={SYSTEM_LOG_PANEL_TITLE_LEVEL} className="mb-4">
        システムログ
      </Title>
      <div
        className="overflow-y-auto border border-gray-200 rounded"
        style={{ height: isMobile ? LOG_PANEL_HEIGHT_MOBILE : LOG_PANEL_HEIGHT_DESKTOP }}
      >
        <List<SystemLogEntry>
          dataSource={[...restoredLogs]}
          renderItem={renderLogItem}
          locale={{ emptyText: 'ログがありません' }}
          size="small"
        />
      </div>
    </Card>
  );
};